/**
 * Atlas History — read-only daily 11PM snapshot viewer.
 *
 * Pick a month/day; if a snapshot exists we render the Atlas board in the
 * exact same UI as the live board, but disabled (no urgency toggles, no drag).
 */
import { useEffect, useMemo, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function fmtKey(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; }
function niceLU(k) {
  if (!k) return "";
  const [y,m,dd] = k.split("-").map(Number);
  return `${m}.${dd}.${String(y).slice(-2)}`;
}
function fmtCapturedAt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const mm = d.getMonth() + 1;
  const dd = d.getDate();
  const yy = String(d.getFullYear()).slice(-2);
  let hh = d.getHours();
  const mi = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  const ap = hh >= 12 ? "PM" : "AM";
  hh = hh % 12 || 12;
  return `${mm}.${dd}.${yy} ${hh}:${mi}:${ss}${ap}`;
}

export default function AtlasHistory() {
  const [cursor, setCursor] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null); // yyyy-mm-dd
  const [availableDates, setAvailableDates] = useState(new Set());
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/atlas/history");
        setAvailableDates(new Set(data?.dates || []));
      } catch (e) {
        // No harm; treat as empty.
      }
    })();
  }, []);

  const loadDate = async (key) => {
    setSelectedDate(key);
    setLoading(true);
    setSnapshot(null);
    try {
      const { data } = await api.get(`/atlas/history/${key}`);
      setSnapshot(data);
    } catch (e) {
      if (e?.response?.status === 404) setSnapshot({ empty: true });
      else { const m = apiErrorMessage(e); if (m) toast.error(m); }
    } finally { setLoading(false); }
  };

  const grid = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const startDay = first.getDay();
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(first);
      d.setDate(1 - startDay + i);
      cells.push(d);
    }
    return cells;
  }, [cursor]);

  return (
    <div className="space-y-3" data-testid="atlas-history">
      <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <Button variant="outline" size="sm" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="atlas-hist-prev">
              <CaretLeft size={14} />
            </Button>
            <h2 className="text-base font-black tracking-tight" data-testid="atlas-hist-title">
              {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
            </h2>
            <Button variant="outline" size="sm" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="atlas-hist-next">
              <CaretRight size={14} />
            </Button>
          </div>
          <div className="text-[11px] text-slate-500">
            {availableDates.size} snapshot{availableDates.size === 1 ? "" : "s"}
          </div>
        </div>
        <div className="grid grid-cols-7 text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
          {DAYS.map((d) => <div key={d} className="px-2 py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-lg overflow-hidden">
          {grid.map((d, i) => {
            const inMonth = d.getMonth() === cursor.getMonth();
            const k = fmtKey(d);
            const hasSnap = availableDates.has(k);
            const isSel = selectedDate === k;
            return (
              <button
                key={i}
                onClick={() => loadDate(k)}
                className={`bg-white min-h-[44px] p-1 flex flex-col items-start ${inMonth ? "" : "bg-slate-50/60"} ${isSel ? "ring-2 ring-inset ring-[#1f64c2]" : ""}`}
                data-testid={`atlas-hist-day-${k}`}
              >
                <span className={`text-[11px] font-semibold ${inMonth ? "text-slate-700" : "text-slate-400"}`}>
                  {d.getDate()}
                </span>
                {hasSnap && <span className="text-[9px] text-emerald-600 font-bold uppercase mt-auto">SNAP&apos;d</span>}
              </button>
            );
          })}
        </div>
      </div>

      {loading && <div className="text-sm text-slate-500 py-6 text-center">Loading snapshot…</div>}
      {snapshot && !snapshot.empty && (
        <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="text-[11px] text-rose-400 font-semibold mb-2" data-testid="atlas-hist-snapshot-label">
            Snapshot from {fmtCapturedAt(snapshot.captured_at) || `${niceLU(snapshot.snapshot_date)} 11PM`}
          </div>
          <ReadonlyBoard snapshot={snapshot} />
        </div>
      )}
      {snapshot && snapshot.empty && (
        <div className="text-sm text-slate-500 italic py-8 text-center" data-testid="atlas-hist-empty">
          No snapshot for {niceLU(selectedDate)} — snapshots are captured nightly at 11PM.
        </div>
      )}
    </div>
  );
}

function ReadonlyBoard({ snapshot }) {
  const columns = snapshot.columns || [];
  const tasks = snapshot.tasks || [];
  const layout = snapshot.layout || { top_panels: ["stops","team","office"], team_order: ["brian","dad"], office_order: ["total","middle","calls"], office_middle_order: ["now","later"] };
  const byKey = Object.fromEntries(columns.map((c) => [c.key, c]));
  const tasksBy = tasks.reduce((acc, t) => { (acc[t.column_key] ||= []).push(t); return acc; }, {});
  Object.keys(tasksBy).forEach((k) => tasksBy[k].sort((a, b) => a.order - b.order));

  const renderCol = (c) => (
    <div key={c.key} className="rounded-lg border border-slate-200 bg-white p-2">
      <div className="text-[11px] font-black uppercase tracking-widest text-slate-700 mb-1.5">{c.name} <span className="text-slate-400 font-normal">· {(tasksBy[c.key] || []).length}</span></div>
      <div className="space-y-1">
        {(tasksBy[c.key] || []).map((t) => (
          <ReadonlyTask key={t.id} task={t} />
        ))}
        {(!tasksBy[c.key] || tasksBy[c.key].length === 0) && (
          <div className="text-[11px] text-slate-300 italic px-1 py-0.5">(empty)</div>
        )}
      </div>
    </div>
  );

  const renderPanel = (key) => {
    if (key === "stops") return renderCol(byKey.stops);
    if (key === "team") {
      const groupName = (columns.find((c) => c.group === "team") || {}).group_name || "TEAM";
      return (
        <div key="team" className="rounded-xl border border-slate-200 bg-white/60 p-2">
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 mb-1.5">{groupName}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {(layout.team_order || ["brian","dad"]).map((k) => byKey[k] && renderCol(byKey[k]))}
          </div>
        </div>
      );
    }
    if (key === "office") {
      const groupName = (columns.find((c) => c.group === "office") || {}).group_name || "OFFICE";
      return (
        <div key="office" className="rounded-xl border border-slate-200 bg-white/60 p-2 lg:col-span-2">
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 mb-1.5">{groupName}</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            {(layout.office_order || ["total","middle","calls"]).map((slot) => {
              if (slot === "middle") {
                return (
                  <div key="middle" className="md:col-span-2 flex flex-col gap-2">
                    {(layout.office_middle_order || ["now","later"]).map((k) => byKey[k] && renderCol(byKey[k]))}
                  </div>
                );
              }
              return byKey[slot] ? renderCol(byKey[slot]) : null;
            })}
          </div>
        </div>
      );
    }
    return null;
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {(layout.top_panels || ["stops","team","office"]).map((k) => renderPanel(k))}
    </div>
  );
}

const URG_MAP = { none: "bg-slate-300 ring-slate-200", green: "bg-emerald-500 ring-emerald-300", yellow: "bg-amber-500 ring-amber-300", red: "bg-rose-500 ring-rose-300" };
function ReadonlyTask({ task }) {
  const u = task.urgency || "none";
  const s = URG_MAP[u] || URG_MAP.none;
  const strike = u === "none";
  return (
    <div className={`flex items-start gap-1.5 rounded-md border border-slate-200 bg-white px-1.5 py-1 text-[12px]`}>
      <span className="mt-[3px] shrink-0">
        {strike ? <span className="text-[10px] font-black tracking-[0.05em] text-slate-400 leading-none">---</span>
                : <span className={`inline-block w-3 h-3 rounded-full ring-2 ${s}`} />}
      </span>
      <span className={`flex-1 min-w-0 break-words ${strike ? "text-slate-500 line-through decoration-slate-400" : "text-slate-800"}`}>{task.text}</span>
    </div>
  );
}
